import { Box, styled, Typography } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AdListingModel from "@/models/AdListingModel";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { AdActions } from "@/app/shared/ad/AdActions";
import ListingCardRibbons from "./ListingCardRibbons";
import Link from "next/link";
import { GalleryCarousel } from "@/app/(browse)/for-sale/details/[slug]/[id]/components/Gallery/GalleryCarousel";
import { AD_IMAGE_RATIO } from "@/services/AdImageConfig";
import Image from "next/image";
import { useCarouselState } from "@/app/layout/useCarouselState";
import { RV_OTT_BLUE_RGB, RV_OTT_PINK_RGB } from "@/app/layout/LayoutConstants";

const Card = styled(Box)();

const Header = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    position: "relative",
    width: "100%",
    aspectRatio: 1 / AD_IMAGE_RATIO,
    borderRadius: 2,
    overflow: "hidden",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    "> *, img": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  }),
);

const Body = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    position: "relative",
    paddingTop: 1,
    marginTop: -2,
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    overflow: "hidden",
  }),
);

// AJB: use RippleCard
export default function ListingCard({
  ad,
  isCompact,
  isSearchResult,
  isOnGrey,
}: {
  ad: AdListingModel;
  isCompact?: boolean;
  isSearchResult?: boolean;
  isOnGrey?: boolean;
}) {
  const { setActiveIndex } = useCarouselState({ key: ad.id });
  const cardOpacity = ad.isInactive ? 0.5 : 1;

  let boxShadow: string | number = 0;
  if (ad.isRvOtt) {
    boxShadow = `0px 6px 6px -3px rgba(${RV_OTT_BLUE_RGB},0.2),0px 10px 14px 1px rgba(${RV_OTT_BLUE_RGB},0.3),0px 4px 18px 3px rgba(${RV_OTT_PINK_RGB},0.4)`;
  } else if (ad.isHighlighted) {
    boxShadow = 10;
  }

  const backgroundRGB = isOnGrey ? "245,245,245" : "255,255,255";

  return (
    <Card
      sx={{
        ...(isSearchResult
          ? {
              boxShadow: boxShadow,
              padding: 0,
              borderRadius: 4,
            }
          : {}),
      }}
    >
      <Header
        sx={{
          opacity: cardOpacity,
        }}
      >
        {!ad.primary_image || ad.gallery.items.length === 0 ? (
          <Box
            component={Link}
            href={ad.url}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              color: "grey.400",
            }}
          >
            <NoPhotographyIcon sx={{ fontSize: "100px" }} />
            <Typography variant="body2">No photography available</Typography>
          </Box>
        ) : (
          <>
            {!isSearchResult ? (
              <Link href={ad.url}>
                <Image
                  src={ad.primary_image}
                  alt={ad.title}
                  fill
                  unoptimized
                  loading="lazy"
                  fetchPriority="high"
                />
              </Link>
            ) : (
              <GalleryCarousel
                gallery={ad.gallery}
                href={ad.url}
                setActiveIndex={setActiveIndex}
              />
            )}
          </>
        )}
      </Header>
      <ListingCardRibbons
        ad={ad}
        isSearchResult={isSearchResult}
        isOnGrey={isOnGrey}
      />
      <Body
        sx={(theme) => ({
          opacity: cardOpacity,
          height: isSearchResult ? "180px" : isCompact ? "120px" : "190px",
          padding:
            ad.isHighlighted && isSearchResult
              ? theme.spacing(1, 2)
              : theme.spacing(1, 0, isSearchResult ? 1 : 0, 0),
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "space-between",
          }}
        >
          <Box component={Link} href={ad.url} sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                flexShrink: 0,
                fontWeight: "bold",
                fontSize: isCompact ? "18px" : undefined,
              }}
            >
              {ad.title}
            </Typography>
          </Box>

          {!isCompact && (
            <Box
              sx={{
                display: "flex",
                fontSize: "25px",
                marginRight: -0.5,
              }}
            >
              <AdActions ad={ad} iconOnly />
            </Box>
          )}
        </Box>
        {ad.detailsLines.map((line, lineIndex) => (
          <Box
            key={lineIndex}
            sx={{
              display: "flex",
              overflow: "hidden",
              flexShrink: 0,
              flexWrap: lineIndex === 0 ? "initial" : "wrap",
            }}
          >
            {line.map((value, valueIndex) => (
              <Box key={valueIndex}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {value}
                  {valueIndex < line.length - 1 && <>&nbsp;·&nbsp;</>}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
        <Typography
          variant="body2"
          sx={{
            color: "grey.600",
            overflow: "hidden",
            maxHeight: "80px",
          }}
        >
          {ad.descriptionShort}
        </Typography>
        <Box
          sx={(theme) => ({
            marginTop: "auto",
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            minWidth: 0,
            whiteSpace: "nowrap",
            position: "relative",
            "::before": {
              content: '" "',
              position: "absolute",
              bottom: "100%",
              left: 0,
              right: 0,
              height: theme.spacing(2),
              background: `linear-gradient(to bottom, rgba(${backgroundRGB},0), rgba(${backgroundRGB},1))`,
            },
          })}
        >
          {ad.location && (
            <>
              {ad.distanceKms !== null ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    display: "inline",
                  }}
                >
                  ~ {ad.distanceKms} kms
                </Typography>
              ) : (
                ""
              )}
              <LocationOnIcon sx={(theme) => ({ ...theme.typography.body1 })} />
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                variant="body2"
              >
                {ad.location}
              </Typography>
            </>
          )}
        </Box>
      </Body>
    </Card>
  );
}
